import { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Table } from '../../components/ui/Table';
import { syncTimestampColumns } from '../../components/ui/SyncTimestamps';
import { FilterBar } from '../../components/ui/FilterBar';
import api from '../../services/api';

const PER_PAGE = 20;
const LOCATION_ROLES = ['loading_port', 'discharge_port', 'final_destination'];
const REFERENCE_TYPES = ['incoterm', 'transport_mode', 'liner_term', 'container_type'];

export default function CatalogsPage() {
  const { t, i18n } = useTranslation();
  const [tab, setTab] = useState('locations');
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('all');
  const [codeInput, setCodeInput] = useState('');
  const [code, setCode] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ page: String(page), limit: String(PER_PAGE) });
    if (filter !== 'all') params.set(tab === 'locations' ? 'role' : 'type', filter);
    if (code) params.set('code', code);
    api.get(`/${tab === 'locations' ? 'locations' : 'reference-data'}?${params}`)
      .then(({ data }) => {
        if (!active) return;
        setRows(Array.isArray(data?.rows) ? data.rows : []);
        setTotal(data?.total ?? 0);
      })
      .catch((err) => { if (active) setError(err.message || t('common.error_occurred')); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [tab, page, filter, code, reload, t]);

  function changeTab(next) {
    setTab(next);
    setPage(1);
    setFilter('all');
    setCode('');
    setCodeInput('');
    setRows([]);
    setTotal(0);
  }

  const locationColumns = [
    { key: 'code', label: t('catalogs.code'), render: (value) => <span className="font-mono font-medium text-primary">{value || '—'}</span> },
    { key: 'name', label: t('catalogs.name'), render: (value) => value || '—' },
    { key: 'type', label: t('catalogs.type'), render: (value) => value || '—' },
    { key: 'roles', label: t('catalogs.roles'), render: (values) => Array.isArray(values) && values.length ? values.map((value) => t(`catalogs.role_values.${value}`, { defaultValue: value })).join(', ') : '—' },
    { key: 'countryName', label: t('catalogs.country'), render: (value, row) => value || row.countryCode || '—' },
    { key: 'unLocationCode', label: t('catalogs.un_code'), render: (value) => value || '—' },
    ...syncTimestampColumns(t, i18n.language),
  ];
  const referenceColumns = [
    { key: 'type', label: t('catalogs.type'), render: (value) => t(`catalogs.type_values.${value}`, { defaultValue: value || '—' }) },
    { key: 'code', label: t('catalogs.code'), render: (value) => <span className="font-mono font-medium text-primary">{value || '—'}</span> },
    { key: 'name', label: t('catalogs.name'), render: (value) => value || '—' },
    ...syncTimestampColumns(t, i18n.language),
  ];

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">{t('catalogs.title')}</h2>
          <p className="text-[12.5px] text-slate-500 mt-0.5">{t('catalogs.subtitle')}</p>
        </div>
        <button onClick={() => setReload((n) => n + 1)} disabled={loading} className="h-8 px-3 flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white text-[12px] font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />{t('common.refresh')}
        </button>
      </div>

      {error && <div className="bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 flex items-center justify-between text-[13px] text-rose-700">
        <span>{error}</span><button onClick={() => setReload((n) => n + 1)} className="font-medium underline">{t('common.retry')}</button>
      </div>}

      <FilterBar groups={[{
        value: tab,
        onChange: changeTab,
        tabs: [
          { key: 'locations', label: t('catalogs.locations') },
          { key: 'reference-data', label: t('catalogs.reference_data') },
        ],
      }]} />

      <div className="flex flex-wrap items-center gap-3">
        <select
          aria-label={tab === 'locations' ? t('catalogs.roles') : t('catalogs.type')}
          value={filter}
          onChange={(event) => { setFilter(event.target.value); setPage(1); }}
          className="h-9 px-2 bg-white border border-slate-200 rounded-lg text-[12.5px] text-slate-700"
        >
          <option value="all">{tab === 'locations' ? t('catalogs.all_roles') : t('catalogs.all_types')}</option>
          {(tab === 'locations' ? LOCATION_ROLES : REFERENCE_TYPES).map((value) => (
            <option key={value} value={value}>
              {t(`catalogs.${tab === 'locations' ? 'role_values' : 'type_values'}.${value}`)}
            </option>
          ))}
        </select>
        <form onSubmit={(event) => { event.preventDefault(); setCode(codeInput.trim()); setPage(1); }} className="flex items-center gap-2">
          <input
            aria-label={t('catalogs.code')}
            value={codeInput}
            onChange={(event) => setCodeInput(event.target.value)}
            placeholder={t('catalogs.code_placeholder')}
            className="h-9 px-3 w-48 bg-white border border-slate-200 rounded-lg text-[12.5px] outline-none focus:border-teal-500"
          />
          <button type="submit" className="h-9 px-3 bg-white border border-slate-200 rounded-lg text-[12px] font-medium text-slate-700 hover:bg-slate-50">{t('catalogs.find_code')}</button>
        </form>
      </div>

      <Table
        columns={tab === 'locations' ? locationColumns : referenceColumns}
        rows={rows}
        rowKey="_id"
        loading={loading}
        emptyState={<p className="px-4 py-8 text-center text-[13px] text-slate-500">{t('catalogs.empty')}</p>}
        page={page}
        total={total}
        perPage={PER_PAGE}
        onPrev={() => setPage((p) => p - 1)}
        onNext={() => setPage((p) => p + 1)}
      />
    </div>
  );
}
